import { useState, useEffect, useCallback } from 'react';
import { useCategoryService, Category } from '../services/categoryService';

/**
 * Custom hook for managing categories in components
 * Provides state management and helper functions for category operations
 */
export const useCategories = (tailNumber?: string) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { getUploadFolders, getStandardCategories } = useCategoryService();

  /**
   * Load upload folders for a specific tail number
   */
  const loadUploadFolders = useCallback(async (tail: string) => {
    if (!tail) return;

    setLoading(true);
    setError(null);
    setCategories([]);

    try {
      const data = await getUploadFolders(tail);
      if (data && data.length > 0) {
        setCategories(data);
      } else {
        setCategories([]);
        setError('Failed to load upload folders');
      }
    } catch (err) {
      setCategories([]);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [getUploadFolders]);

  /**
   * Load standard categories
   */
  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCategories([]);

    try {
      const data = await getStandardCategories();
      if (data && data.length > 0) {
        setCategories(data);
      } else {
        setCategories([]);
        setError('Failed to load categories');
      }
    } catch (err) {
      setCategories([]);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [getStandardCategories]);

  /**
   * 
   * Switch data source based on tailNumber
   */
  useEffect(() => {
    if (tailNumber && tailNumber.length > 0) {
      loadUploadFolders(tailNumber);
    } else {
      loadCategories();
    }
  }, [tailNumber, loadUploadFolders, loadCategories]);

  /**
   * Helpers
   */
  const getUniqueCodes = useCallback(() => {
    const uniqueCodes = categories.map(c => c.code).filter((v, i, a) => a.indexOf(v) === i);
    return uniqueCodes.sort();
  }, [categories]);

    /**
   * Get category by code
   */
  const getCategoryByCode = useCallback((code: string) => {
    return categories.find(c => c.code === code) || null;
  }, [categories]);

    /**
   * Get all categories with a specific code (e.g., F or I might have multiple)
   */
  const getCategoriesByCode = useCallback((code: string) => {
    return categories.filter(c => c.code === code);
  }, [categories]);

    /**
   * Check if a category requires serial number
   */
  const requiresSerialNumber = useCallback((code: string) => {
    const category = getCategoryByCode(code);
    return category?.requiresSerialNumber || false;
  }, [getCategoryByCode]);

  /**
   * Format category for dropdown display
   */
  const formatLabel = useCallback((category: Category) => {
    return category.name;
  }, []);
  /**
   * Get categories formatted for MUI Select options
   */
  const getDropdownOptions = useCallback(() => {
    return categories.map(category => ({
      value: category.code,
      label: formatLabel(category),
      requiresSerialNumber: category.requiresSerialNumber,
    }));
  }, [categories, formatLabel]);
 /**
   * Get unique categories formatted for MUI Select (one per code)
   */
  const getUniqueDropdownOptions = useCallback(() => {
    const uniqueCodes = getUniqueCodes();
    return uniqueCodes.map(code => {
      const category = getCategoryByCode(code);
      return {
        value: code,
        label: category ? formatLabel(category) : code,
        requiresSerialNumber: category?.requiresSerialNumber || false,
      };
    });
  }, [getUniqueCodes, getCategoryByCode, formatLabel]);
 /**
   * Get top-level categories (no parent)
   */
const getTopLevelCategories = useCallback(() => {
  return categories.filter(c =>
    !c.parentCode ||c.parentCode === '');
}, [categories]);

  /**
   * Get children categories by parent code
   */
  const getChildrenByParentCode = useCallback((parentCode: string) => {
    return categories.filter(c => c.parentCode === parentCode);
  }, [categories]);

  return {
    categories,
    loading,
    error,

    // Helpers
    getUniqueCodes,
    getCategoryByCode,
    getCategoriesByCode,
    requiresSerialNumber,
    formatLabel,
    getDropdownOptions,
    getUniqueDropdownOptions,
    getTopLevelCategories,
    getChildrenByParentCode,
  };
};
